import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useData } from 'vike-react/useData'

import {
  absoluteUrl,
  breadcrumbSchema,
  itemListSchema,
  JsonLd,
} from '../../../components/JsonLd'
import { Section } from '../../../components/Section'
import { SectionLastCTA } from '../../../components/SectionLastCTA'
import { SlitePage } from '../../../components/SlitePage'
import { useRoutes } from '../../../routes'
import { filterIntegrations } from '../filterIntegrations'
import { IntegrationGrid } from '../IntegrationGrid'
import { IntegrationsHeader } from '../IntegrationsHeader'
import { IntegrationsSidebar } from '../IntegrationsSidebar'
import type { Data } from './+data'

export default function ClientsPage() {
  const intl = useIntl()
  const routes = useRoutes()

  const { integrations } = useData<Data>()
  const [searchQuery, setSearchQuery] = useState('')

  const filteredIntegrations = useMemo(
    () => filterIntegrations(integrations, searchQuery),
    [integrations, searchQuery]
  )

  const pageTitle = intl.formatMessage({
    id: 'integrations.clients.title',
    defaultMessage: 'Use Slite AI from any tool',
  })

  const pageDescription = intl.formatMessage({
    id: 'integrations.clients.description',
    defaultMessage:
      'Ask Slite from Claude, ChatGPT, Slack, Zapier and any tool that supports MCP.',
  })

  const integrationsLabel = intl.formatMessage({
    id: 'integrations.breadcrumb.integrations',
    defaultMessage: 'Integrations',
  })

  const itemList = itemListSchema({
    items: integrations.map((integration) => ({
      name: integration.name,
      url: absoluteUrl(
        routes.integrations.detail({ slug: integration.slug.current })
      ),
    })),
  })

  const breadcrumbs = breadcrumbSchema({
    items: [
      { name: 'Slite', url: absoluteUrl('/') },
      {
        name: integrationsLabel,
        url: absoluteUrl(routes.integrations.index()),
      },
      {
        name: pageTitle,
        url: absoluteUrl(routes.integrations.clients()),
      },
    ],
  })

  return (
    <SlitePage
      canonicalUrl={routes.integrations.clients()}
      background="moon-dust-lighter"
      title={pageTitle}
      description={pageDescription}
    >
      <JsonLd data={itemList} />
      <JsonLd data={breadcrumbs} />
      <IntegrationsHeader
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
      />
      <Section background="moon-dust-lighter" containerTopPadding={0}>
        <div className="grid-12 grid-gap-40">
          <div className="grid-span-3 grid-span-12-small">
            <IntegrationsSidebar current="client" />
          </div>
          <div className="grid-span-9 grid-span-12-small">
            <IntegrationGrid integrations={filteredIntegrations} columns={3} />
          </div>
        </div>
      </Section>
      <SectionLastCTA background="moon-dust-lighter" />
    </SlitePage>
  )
}
